import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useOrganization } from '../../contexts/OrganizationContext';
import { Spinner } from '../../design-system';

interface OrganizationRequiredRouteProps {
  children?: React.ReactNode;
}

/**
 * A route component that checks if the user is authenticated AND belongs to an organization.
 * If not authenticated → redirects to homepage
 * If authenticated but no organization → redirects to organization setup
 */
const OrganizationRequiredRoute = ({ children }: OrganizationRequiredRouteProps) => {
  const { isAuthenticated, isLoading: authLoading, impersonationContext } = useAuth();
  const { currentOrganization, hasRealOrganizations, isLoading: orgLoading } = useOrganization();
  const location = useLocation();

  const isLoading = authLoading || orgLoading;

  // Show loading state while authentication/organization is being checked
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Spinner size={48} className="mx-auto mb-4" />
          <p className="text-ui-text-secondary">Loading...</p>
        </div>
      </div>
    );
  }

  // If not authenticated, redirect to homepage
  if (!isAuthenticated) {
    console.log('User not authenticated, redirecting to homepage from:', location.pathname);
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  // If authenticated but no real organizations, redirect to organization setup
  // unless the user has a private demo workspace.
  // UNLESS we're impersonating - in that case, show the user's real state
  if (
    !hasRealOrganizations &&
    !currentOrganization?.is_demo &&
    !impersonationContext.isImpersonating
  ) {
    // Don't redirect if already on the setup page
    if (location.pathname !== '/organization-setup') {
      console.log('User has no organizations, redirecting to setup from:', location.pathname);
      return <Navigate to="/organization-setup" state={{ from: location }} replace />;
    }
  }

  // If authenticated and has organizations (or impersonating), render the protected content
  return children ? <>{children}</> : <Outlet />;
};

export default OrganizationRequiredRoute;
